/**
 * Alpine component for the passkey settings panel.
 *
 * Renders the user's registered passkeys, supports add/rename/delete,
 * and displays add/remove status messages. Talks to the
 * /v1/webauthn-credentials* and /v1/webauthn-registration* endpoints
 * via the shared ApiClient. WebAuthn ceremony helpers come from
 * window.WebAuthnHelpers.
 *
 * Mounted on the security-section root in settings.twig:
 *   <div x-data="passkeySettings({ defaultName, confirmDelete, ... })">
 */

function helpers() {
    return window.WebAuthnHelpers;
}

function translate(domain, key, fallback) {
    return helpers().translate(domain, key, fallback);
}

function formatDate(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso.replace(' ', 'T'));
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString();
    } catch (_e) {
        return iso;
    }
}

function suggestedPasskeyName(defaultName) {
    const h = helpers();
    const browser = h.detectBrowserLabel(navigator.userAgent);
    const platformHint = navigator.userAgentData?.platform || navigator.platform || '';
    const platform = h.detectPlatformLabel(navigator.userAgent, platformHint, navigator.maxTouchPoints);
    return h.getSuggestedPasskeyName(defaultName, browser, platform);
}

export function passkeySettings(strings = {}) {
    return {
        loading: true,
        unsupported: false,
        passkeys: [],
        currentPassword: '',
        status: { text: '', isError: false },
        editingId: null,
        editingName: '',

        defaultName: strings.defaultName || 'Passkey',
        confirmDelete: strings.confirmDelete || 'Remove this passkey?',
        passwordRequiredMsg: strings.passwordRequired || '',
        passwordInvalidMsg: strings.passwordInvalid || '',
        passwordNotSetMsg: strings.passwordNotSet || '',

        init() {
            if (!helpers().isWebAuthnSupported()) {
                this.unsupported = true;
                this.loading = false;
                return;
            }
            void this.refresh();
        },

        formatRowDate(cred) {
            const created = formatDate(cred.created_at);
            const last = cred.last_used_at ? formatDate(cred.last_used_at) : '';
            return last ? `${created} · ${last}` : created;
        },

        rowName(cred) {
            return cred.name || this.defaultName;
        },

        showStatus(text, isError) {
            this.status = { text, isError: !!isError };
        },

        clearStatus() {
            this.status = { text: '', isError: false };
        },

        requirePassword() {
            if (this.currentPassword) return this.currentPassword;
            this.showStatus(
                this.passwordRequiredMsg
                    || translate('settings', 'validation_enter_current_password', 'Enter your current password.'),
                true
            );
            return null;
        },

        passkeySetupError() {
            return translate(
                'auth',
                'passkey_setup_error_generic',
                'Could not add a passkey. This device may already be set up.'
            );
        },

        passkeyAlreadyExistsError() {
            return translate(
                'auth',
                'passkey_setup_error_already_exists',
                'This device already has a passkey for your account.'
            );
        },

        passkeyRateLimitError() {
            return translate(
                'auth',
                'passkey_error_rate_limited',
                'Too many attempts. Wait a moment and try again.'
            );
        },

        passkeyPasswordNotSetError() {
            return this.passwordNotSetMsg
                || translate(
                    'auth',
                    'passkey_error_password_not_set',
                    'Set a password on your account first, then add a passkey.'
                );
        },

        async errorForResponse(res, fallback) {
            const classification = await helpers().classifyPasskeyError(res);
            switch (classification.kind) {
                case 'rate_limited':
                    return this.passkeyRateLimitError();
                case 'password_not_set':
                    return this.passkeyPasswordNotSetError();
                case 'password_invalid':
                    return this.passwordInvalidMsg || fallback;
                default:
                    return fallback;
            }
        },

        async refresh() {
            try {
                if (!window.ApiClient) {
                    this.passkeys = [];
                    return;
                }
                const res = await window.ApiClient.request('/webauthn-credentials', { method: 'GET' });
                if (!res.ok) {
                    this.passkeys = [];
                    return;
                }
                const data = await res.json();
                this.passkeys = Array.isArray(data.credentials) ? data.credentials : [];
                if (this.editingId && !this.passkeys.some((c) => String(c.id) === this.editingId)) {
                    this.cancelRename();
                }
            } catch (e) {
                if (window.console) console.warn('passkey-settings: list failed', e);
                this.passkeys = [];
            } finally {
                this.loading = false;
            }
        },

        async addPasskey() {
            if (!helpers().isWebAuthnSupported() || !window.ApiClient) return;

            this.clearStatus();
            const currentPassword = this.requirePassword();
            if (!currentPassword) return;

            try {
                const optionsRes = await window.ApiClient.request('/webauthn-registration/options', { method: 'POST' });
                if (!optionsRes.ok) {
                    this.showStatus(await this.errorForResponse(optionsRes, this.passkeySetupError()), true);
                    return;
                }
                const options = await optionsRes.json();
                const decoded = helpers().decodeCreateOptions(options.publicKey);

                let credential;
                try {
                    credential = await navigator.credentials.create({ publicKey: decoded });
                } catch (e) {
                    if (e && (e.name === 'NotAllowedError' || e.name === 'AbortError')) {
                        return;
                    }
                    const message = e && e.name === 'InvalidStateError'
                        ? this.passkeyAlreadyExistsError()
                        : this.passkeySetupError();
                    this.showStatus(message, true);
                    if (window.console) console.warn('passkey-settings:', e.name, e.message);
                    return;
                }

                if (!credential) return;

                const formData = new FormData();
                formData.append('challengeId', options.challengeId);
                formData.append('clientDataJSON', helpers().base64UrlEncode(credential.response.clientDataJSON));
                formData.append('attestationObject', helpers().base64UrlEncode(credential.response.attestationObject));
                formData.append('currentPassword', currentPassword);
                formData.append('name', suggestedPasskeyName(this.defaultName));

                const verifyRes = await window.ApiClient.request('/webauthn-registration', {
                    method: 'POST',
                    body: formData,
                });
                if (!verifyRes.ok) {
                    this.showStatus(await this.errorForResponse(verifyRes, this.passkeySetupError()), true);
                    return;
                }

                this.showStatus(
                    translate('auth', 'passkey_added_toast', 'Passkey added. Your password still works.'),
                    false
                );
                this.currentPassword = '';
                await this.refresh();
            } catch (e) {
                this.showStatus(this.passkeySetupError(), true);
                if (window.console) console.warn('passkey-settings:', e);
            }
        },

        startRename(cred) {
            this.editingId = String(cred.id);
            this.editingName = cred.name || this.defaultName;
            this.clearStatus();
            // The rename input lives inside x-for, so $refs cannot point
            // to a per-row element. Look it up by data attribute on the
            // component root once Alpine has rendered the edit row.
            this.$nextTick(() => {
                const input = this.$root.querySelector(`[data-rename-input="${this.editingId}"]`);
                if (input) {
                    input.focus();
                    input.select();
                }
            });
        },

        cancelRename() {
            this.editingId = null;
            this.editingName = '';
        },

        isEditing(cred) {
            return this.editingId === String(cred.id);
        },

        async saveRename(cred) {
            if (!window.ApiClient) return;
            const currentName = cred.name || this.defaultName;
            const trimmed = this.editingName.trim();
            if (trimmed === '' || trimmed === currentName) {
                this.cancelRename();
                return;
            }

            const currentPassword = this.requirePassword();
            if (!currentPassword) return;

            const body = new FormData();
            body.append('name', trimmed);
            body.append('currentPassword', currentPassword);
            try {
                const res = await window.ApiClient.request(
                    `/webauthn-credentials/${encodeURIComponent(cred.id)}`,
                    { method: 'PATCH', body }
                );
                if (res.ok) {
                    this.currentPassword = '';
                    this.cancelRename();
                    await this.refresh();
                    return;
                }
                this.showStatus(await this.errorForResponse(res, this.passkeySetupError()), true);
            } catch (e) {
                this.showStatus(this.passkeySetupError(), true);
                if (window.console) console.warn('passkey-settings:', e);
            }
        },

        async deletePasskey(cred) {
            if (!window.ApiClient) return;
            const performDelete = async () => {
                const currentPassword = this.requirePassword();
                if (!currentPassword) return;

                const body = new URLSearchParams({ currentPassword });
                const res = await window.ApiClient.request(
                    `/webauthn-credentials/${encodeURIComponent(cred.id)}`,
                    {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body,
                    }
                );
                if (res.ok || res.status === 204) {
                    this.currentPassword = '';
                    await this.refresh();
                    this.showStatus(
                        translate('auth', 'passkey_removed_toast', 'Passkey removed.'),
                        false
                    );
                    return;
                }
                this.showStatus(await this.errorForResponse(res, this.passkeySetupError()), true);
            };

            if (window.ConfirmModal && typeof window.ConfirmModal.show === 'function') {
                window.ConfirmModal.show({
                    message: this.confirmDelete,
                    type: 'danger',
                    onConfirm: () => { void performDelete(); },
                });
                return;
            }

            // Fallback if confirm-modal.js has not loaded yet — use the
            // platform dialog so destructive intent is still gated.
            if (window.confirm(this.confirmDelete)) {
                await performDelete();
            }
        },
    };
}
